import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import Button from "@shared/v2/Button";
import TextButton from "@shared/v2/TextButton";
import { useLocalStorage } from "@shared/hookHelpers";
import { mapAlertToState, mapStateToAlert } from "@shared/ListingAlertHelpers";
import { truncateText } from "./helper";
import AddListingAlertModal from "../modals/AddListingAlertModal";
import EditListingAlertModal from "../modals/EditListingAlertModal";
import * as listingAlertService from "./service";
import DeleteListingAlertModal from "../modals/DeleteListingAlertModal";
import CollapsibleElement from "../CollapsibleElement";
import * as actions from "../../actions/personDetailActionCreators";
import Loading from "../../../Goals/components/Loader";
import ListingAlertCardView from "./ListingAlertCardView";
import CollectionCard from "./CollectionCard";
import ViewAllListingAlertsModal from "../modals/ViewAllListingAlertsModal";
import useAlertInitialization from "./useAlertInitialization";

const ListingAlertCard = ({ openPlatformCTA, onToggleCollectionNotification }) => {
  const dispatch = useDispatch();
  const [
    {
      editListingAlertModalOpen,
      deleteListingAlertModalOpen,
      deleteAlertPending,
      deleteAlertIndex,
      indexOfAlertToEdit,
      listingAlerts,
      listingAlertsModalOpen,
      mlsIds,
      websiteUrl,
    },
    _setState,
  ] = useState({
    editListingAlertModalOpen: false,
    deleteListingAlertModalOpen: false,
    deleteAlertPending: false,
    indexOfAlertToEdit: 0,
    listingAlerts: [],
    listingAlertsModalOpen: false,
    mlsIds: null,
    websiteUrl: "",
  });
  const setState = (newState) => _setState((prevState) => ({ ...prevState, ...newState }));
  const featureFlags = useSelector((state) => state.layout.featureFlags);
  const basePerson = useSelector((state) => state.personDetail.person);
  const person = basePerson.data?.attributes;
  const geoCodeAccountAddress = basePerson.meta?.geo_code_account_address;
  const addListingAlertModalOpen = useSelector((state) => state.personDetail.addListingAlertModalOpen);
  const [isDismiss, setIsDismiss] = useLocalStorage("dismissAlert", false);
  const doesLeadHaveApp = !!person?.home_app_last_login;

  const { errorMessage, propertyTypes } = useAlertInitialization({
    personId: person?.id,
    mlsIds,
  });
  const openAddListingAlertModal = () => {
    dispatch(actions.openAddListingAlertModal());
  };

  const listingAlertButtonText = featureFlags.platformAccount ? "+ ADD" : "LEARN MORE";
  const listingAlertButtonClick = featureFlags.platformAccount ? openAddListingAlertModal : openPlatformCTA;
  // LEARN MORE opens a platform upsell, which needs no email address.
  const addDisabledForEmail = featureFlags.platformAccount && !person?.main_email;

  useEffect(() => {
    if (!person) return;
    getAlerts();
  }, [person?.main_email]);

  useEffect(() => {
    if (addListingAlertModalOpen) ensureMlsIdsLoaded();
  }, [addListingAlertModalOpen]);

  const getAlerts = async ({ includeMlsIds = false } = {}) => {
    try {
      const response = await listingAlertService.getAlerts(person.slug, { includeMlsIds });
      setState({
        listingAlerts: response.data.alerts,
        websiteUrl: response.data.website_url,
        ...(includeMlsIds && { mlsIds: response.data.mls_ids }),
      });
    } catch (e) {
      console.log(e);
    }
  };

  const ensureMlsIdsLoaded = async () => {
    if (mlsIds !== null) return;
    await getAlerts({ includeMlsIds: true });
  };

  const dismissAlertCard = () => {
    setIsDismiss(true);
  };

  const toggleNotificationCollection = async (alertIndex, alertId, alertData, value) => {
    try {
      if (errorMessage) {
        onToggleCollectionNotification({ show: true, type: "error", msg: errorMessage });
        setTimeout(() => {
          onToggleCollectionNotification({ show: false, type: "", msg: "" });
        }, 5000);
        return;
      }
      const alert = {
        ...alertData,
        ...(doesLeadHaveApp && { activeForAppNotifications: value }),
        activeForEmails: value,
      };
      const payload = mapAlertToState(alert);

      const response = await listingAlertService.updateAlert(person?.id, alertId, mapStateToAlert(payload));
      const updatedListingAlerts = [...listingAlerts];
      updatedListingAlerts[alertIndex] = {
        ...updatedListingAlerts[alertIndex],
        ...(doesLeadHaveApp && {
          activeForAppNotifications: alert.activeForAppNotifications,
        }),
        activeForEmails: alert.activeForEmails,
      };

      setState({
        listingAlerts: updatedListingAlerts,
      });
      const msg = value
        ? `Lead will be notified of updates to properties in ${truncateText(alertData.searchName, 30)}`
        : `Lead will stop receiving notifications of updates to properties in ${truncateText(alertData.searchName, 30)}`;
      onToggleCollectionNotification({ show: true, type: "success", msg: msg });
      setTimeout(() => {
        onToggleCollectionNotification({ show: false, type: "", msg: "" });
      }, 5000);
    } catch (e) {
      const msg = `An issue occurred when updating the notification setting for ${truncateText(alertData.searchName, 30)}. Try again later.`;
      onToggleCollectionNotification({ show: true, type: "error", msg: msg });
      setTimeout(() => {
        onToggleCollectionNotification({ show: false, type: "", msg: "" });
      }, 5000);
      console.log(e);
    }
  };

  const onDismiss = () => {
    dismissAlertCard();
  };

  const onToggleNotification = (alertIndex, value) => {
    toggleNotificationCollection(
      alertIndex,
      listingAlerts[alertIndex].alertId,
      listingAlerts[alertIndex],
      value,
    );
  };

  const renderLaCTA = () => {
    return (
      <div className="tw-text-[12px] tw-text-gray-75">
        Use Listing Alert emails to regularly send listings that your buyers might be interested in. When a
        contact saves a search on your website, it automatically creates a Listing Alert.
      </div>
    );
  };

  const renderNoEmail = () => {
    return (
      <div className="tw-text-[12px] tw-text-gray-75">
        Enter an email address for this contact in order to send Listing Alerts.
      </div>
    );
  };

  const openListingAlertModal = async () => {
    await ensureMlsIdsLoaded();
    setState({ listingAlertsModalOpen: true });
  };

  const closeListingAlertModal = () => {
    setState({ listingAlertsModalOpen: false });
  };

  const openEditListingAlertModal = async (alertIndex) => {
    await ensureMlsIdsLoaded();
    setState({ editListingAlertModalOpen: true, indexOfAlertToEdit: alertIndex });
  };

  const closeEditListingAlertModal = (status) => {
    setState({ editListingAlertModalOpen: false, indexOfAlertToEdit: 0 });
    if (status === "success") {
      getAlerts();
    }
  };

  const openDeleteListingAlertModal = (deleteAlertIndex) => {
    setState({ deleteListingAlertModalOpen: true, deleteAlertIndex });
  };

  const closeDeleteListingAlertModal = () => {
    setState({ deleteListingAlertModalOpen: false, deleteAlertIndex: null });
  };

  const emptyStateOrAlert = (listingAlerts) => {
    // Only assert "no email" once the person has loaded, or the message flashes for everyone.
    if (person && !person.main_email && listingAlerts?.length === 0) {
      return renderNoEmail();
    }

    if (listingAlerts?.length === 0) {
      return renderLaCTA();
    }

    return listingAlerts
      ?.slice(0, 2)
      .map((alert, alertIndex) =>
        alert?.source === "property_collections" ? (
          <CollectionCard
            key={alert.alertId}
            alert={alert}
            index={alertIndex}
            websiteUrl={websiteUrl}
            onToggleNotification={onToggleNotification}
            doesLeadHaveApp={doesLeadHaveApp}
          />
        ) : (
          <ListingAlertCardView
            key={alert.alertId}
            openEditListingAlertModal={openEditListingAlertModal}
            openDeleteListingAlertModal={openDeleteListingAlertModal}
            alert={alert}
            index={alertIndex}
            websiteUrl={websiteUrl}
          />
        ),
      );
  };

  const deleteAlert = (alertIndex) => {
    const alertId = listingAlerts[alertIndex].alertId;
    const url = `/people/${person?.id}/listing_alerts/${alertId}`;

    setState({ deleteAlertPending: true });

    axios
      .delete(url, { params: { authenticity_token: ReactOnRails.authenticityToken() } })
      .then(() => {
        setState({
          listingAlerts: listingAlerts?.filter((alert) => alert.alertId !== alertId),
          deleteAlertIndex: null,
          deleteListingAlertModalOpen: false,
          deleteAlertPending: false,
        });
      })
      .catch((response) => console.log(response))
      .finally(() => setState({ deleteAlertPending: false }));
  };

  const openEditFromViewAll = (alertIndex) => {
    setState({
      indexOfAlertToEdit: alertIndex,
      editListingAlertModalOpen: true,
      listingAlertsModalOpen: false,
    });
  };

  const openAddFromViewAll = () => {
    setState({ listingAlertsModalOpen: false });
    dispatch(actions.openAddListingAlertModal());
  };

  const closeAddListingAlertModal = (status) => {
    dispatch(actions.closeAddListingAlertModal());

    if (status === "success") {
      getAlerts();
    }
  };

  return (
    <CollapsibleElement
      id="pdp-listing-alerts"
      title="Listing Alerts"
      buttonText={listingAlertButtonText}
      buttonClickFunction={listingAlertButtonClick}
      buttonDisabled={addDisabledForEmail}
      // Disabling before the person loads is safe; claiming they have no email is not.
      buttonDisabledTooltip={person ? "Contact does not have an email address" : null}
    >
      {!basePerson.data && <Loading />}

      <div id="person-listing-alerts-card" className="tw-flex tw-flex-col tw-gap-[16px]">
        {listingAlerts?.some((alert) => alert?.source === "property_collections") &&
          !isDismiss && (
            <div className="tw-py-[12px] tw-px-[16px] tw-border-solid tw-border-[1px] tw-text-brand-success tw-border-[#DEF3F3] tw-bg-blue-10 tw-rounded-[4px] tw-flex tw-items-center">
              <div>
                <div className="tw-mb-[4px] tw-text-[12px] tw-leading-[16px] tw-text-brand-success">
                  <span className="tw-font-bold">Collections</span> currently uses the Listing Alert feature
                  to deliver property updates to leads.
                </div>
                <div>
                  <Button
                    schema="secondary"
                    onClick={() =>
                      window.open(
                        "https://www.brivityknowledge.com/docs/how-collections-work-in-brivity",
                        "_blank",
                      )
                    }
                  >
                    <span>Learn more</span>
                  </Button>
                  <Button schema="secondary" onClick={onDismiss} className="tw-ml-[4px]">
                    <span>dismiss</span>
                  </Button>
                </div>
              </div>
            </div>
          )}

        {emptyStateOrAlert(listingAlerts)}
        {listingAlerts?.length > 2 && (
          <TextButton className="tw-self-start" onClick={openListingAlertModal}>
            View All
          </TextButton>
        )}
        {listingAlertsModalOpen && (
          <ViewAllListingAlertsModal
            onClose={closeListingAlertModal}
            listingAlerts={listingAlerts}
            openEditModal={openEditFromViewAll}
            handleDelete={deleteAlert}
            mlsIds={mlsIds}
            websiteUrl={websiteUrl}
            openAddModal={openAddFromViewAll}
            onDismiss={onDismiss}
            isDismiss={isDismiss}
            onToggleNotification={onToggleNotification}
            doesLeadHaveApp={doesLeadHaveApp}
          />
        )}
        {addListingAlertModalOpen && mlsIds !== null && (
          <AddListingAlertModal
            person={person}
            closeListingAlertModal={(status) => closeAddListingAlertModal(status)}
            mlsIds={mlsIds}
            geoCodeAccountAddress={geoCodeAccountAddress}
          />
        )}
        {editListingAlertModalOpen && mlsIds !== null && (
          <EditListingAlertModal
            person={person}
            closeListingAlertModal={closeEditListingAlertModal}
            alert={listingAlerts[indexOfAlertToEdit]}
            mlsIds={mlsIds}
            geoCodeAccountAddress={geoCodeAccountAddress}
          />
        )}
        <DeleteListingAlertModal
          loading={deleteAlertPending}
          open={deleteListingAlertModalOpen}
          onClose={closeDeleteListingAlertModal}
          onDelete={() => deleteAlert(deleteAlertIndex)}
        />
      </div>
    </CollapsibleElement>
  );
};

export default ListingAlertCard;
